import React, {useEffect, useState} from 'react';
import {Table, Button, Modal} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import {
  PageSizeArray,
  PaginationPosition,
  attendanceTypeFilter,
} from '../../../Util/commonUtility';
import {useSelector} from 'react-redux';
import useLocalization from '../../../Localization';
import moment from 'moment';
import HeaderSearchCard from '../../CommonComponents/HeaderSearchCard';
import DynamicSearchForm from '../../CommonComponents/DynamicSearch';
import {CSVLink} from 'react-csv';
import {usePermissions} from '../../../hooks/usePermission';
import {UPDATE} from '../../../Constant/enums';
import AdminActionsDropdown from '../../CommonComponents/AdminActionsDropdown/AdminActionsDropdown';

const WaitingListComponent = (props) => {
  let navigate = useNavigate();
  const {ApplicationString} = useLocalization();
  const pageSize = useSelector(
    ({pageSizes}) => pageSizes.pageSizes.waitingList,
  );
  const {
    waitingListData,
    waitingListDataForCSV,
    csvDataLoading,
    fetchWaitingListForCSV,
    paging,
    handlePaginationChange,
    sortParams,
    handleTableChange,
    onSearch,
    searchValues,
    onMoveToRegistration,
    movingWaitingListId,
    onCancelWaitingListEntry,
    cancellingWaitingListId,
  } = props;

  const headers = [
    {label: 'First Name', key: 'first_name'},
    {label: 'Last Name', key: 'last_name'},
    {label: 'Full Name', key: 'full_name'},
    {label: 'Email', key: 'email'},
    {label: 'Registration Type', key: 'registration_type'},
    {label: 'Attendance Type', key: 'attendance_type'},
    {label: 'Sponsorship Type', key: 'sponsorship_type'},
    {label: 'Date', key: 'date'},
    {label: 'Local Chapter', key: 'local_chapter'},
    {label: 'Attendee Added', key: 'attendee_added'},
    {label: 'Cancelled', key: 'cancelled'},
  ];

  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const {checkPermission} = usePermissions(userInfo?.role);
  const canManageWaitingList = checkPermission('events_registrations', UPDATE);

  const isActiveWaitingListEntry = (record) =>
    !record?.isAttendeeRegistered && !record?.isCancelled;

  const [showWaitingListMessage, setShowWaitingListMessage] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const handleWaitingListMessageClose = () => {
    setShowWaitingListMessage(false);
  };
  const toggleExpand = () => {
    setExpanded(!expanded);
  };
  const waitingListFilters = [
    {
      key: 'isAttendeeRegistered',
      label: ApplicationString['waitingList.attendeeAdded'] || 'Attendee Added',
      name: 'isAttendeeRegistered',
      type: 'dropdown',
      options: [
        {label: 'Yes', value: 'true'},
        {label: 'No', value: 'false'},
      ],
      initialValue: [],
    },
    {
      key: 'isCancelled',
      label: ApplicationString['waitingList.cancelled'] || 'Cancelled',
      name: 'isCancelled',
      type: 'dropdown',
      options: [
        {label: 'Yes', value: 'true'},
        {label: 'No', value: 'false'},
      ],
      initialValue: [],
    },
    attendanceTypeFilter,
  ];

  const getRegistrationTypeLabel = (record) => {
    const raw = (record?.attendance_type ?? '').toString().trim().toLowerCase();
    if (raw.includes('web')) {
      return (
        ApplicationString['waitingList.attendanceType.webcast'] || 'Webcast'
      );
    }
    if (raw.includes('workshop')) {
      return ApplicationString['waitingList.registrationType.workshop'] || 'Workshop';
    }
    if (!raw) {
      return '-';
    }
    return (
      ApplicationString['waitingList.attendanceType.inPerson'] || 'In person'
    );
  };

  const getOptionTypeLabel = (text) => {
    if (!text) {
      return '-';
    }
    const normalized = text.toString().trim().toLowerCase();
    if (normalized === 'registration') {
      return ApplicationString['waitingList.optionType.registration'] || 'Registration';
    }
    if (normalized === 'sponsorship') {
      return ApplicationString['waitingList.optionType.sponsorship'] || 'Sponsorship';
    }
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    searchValues && setExpanded(true);
  }, [searchValues]);

  const formattedWaitingListCSVData = waitingListDataForCSV?.map((item) => ({
    first_name: item?.first_name || '',
    last_name: item?.last_name || '',
    full_name: [item?.first_name, item?.last_name]
      .map((namePart) => (namePart || '').trim())
      .filter(Boolean)
      .join(' '),
    email: item?.email || '',
    registration_type: item?.registration_type
      ? item.registration_type.charAt(0).toUpperCase() +
        item.registration_type.slice(1)
      : '-',
    attendance_type: getRegistrationTypeLabel(item),
    sponsorship_type:
      (item?.registration_type || '').toString().toLowerCase() ===
      'registration'
        ? '-'
        : item?.event_option_name || '-',
    date: item?.created_at || '-',
    local_chapter: item?.local_chapter || '',
    attendee_added: item?.isAttendeeRegistered ? 'Yes' : 'No',
    cancelled: item?.isCancelled ? 'Yes' : 'No',
  }));

  const columns = [
    {
      title: ApplicationString['waitingList.memberName'] || 'Member Name',
      dataIndex: 'user_name',
      key: 'user_name',
      sorter: true,
      sortOrder: sortParams.field === 'user_name' && sortParams.order,
      render: (text) => text || 'N/A',
    },
    {
      title: ApplicationString['waitingList.memberEmail'] || 'Member Email',
      dataIndex: 'user_email',
      key: 'user_email',
      sorter: true,
      sortOrder: sortParams.field === 'user_email' && sortParams.order,
      render: (text) => text || 'N/A',
    },
    {
      title: ApplicationString['waitingList.firstName'],
      dataIndex: 'first_name',
      key: 'first_name',
      sorter: true,
      sortOrder: sortParams.field === 'first_name' && sortParams.order,
      render: (_, record) => `${record.first_name} ${record.last_name}`,
    },
    {
      title: ApplicationString['waitingList.email'],
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      sortOrder: sortParams.field === 'email' && sortParams.order,
    },
    {
      title: ApplicationString['waitingList.optionType'] || 'Option Type',
      dataIndex: 'registration_type',
      key: 'registration_type',
      width: 110,
      sorter: true,
      sortOrder: sortParams.field === 'registration_type' && sortParams.order,
      render: (text) => getOptionTypeLabel(text),
    },
    {
      title: ApplicationString['waitingList.registrationType'],
      dataIndex: 'attendance_type',
      key: 'attendance_type',
      width: 120,
      sorter: true,
      sortOrder: sortParams.field === 'attendance_type' && sortParams.order,
      render: (_, record) => getRegistrationTypeLabel(record),
    },
    {
      title: ApplicationString['waitingList.eventOption'] || 'Event Option',
      dataIndex: 'event_option_name',
      key: 'event_option_name',
      width: 140,
      sorter: true,
      sortOrder: sortParams.field === 'event_option_name' && sortParams.order,
      render: (text) => text || '-',
    },
    {
      title: ApplicationString['waitingList.date'],
      dataIndex: 'created_at',
      key: 'created_at',
      width: 96,
      sorter: true,
      sortOrder: sortParams.field === 'created_at' && sortParams.order,
      render: (text) => {
        if (!text) return '-';
        return moment(text).format('MM/DD/YY');
      },
    },
    {
      title: ApplicationString['waitingList.localChapter'],
      dataIndex: 'local_chapter',
      key: 'local_chapter',
      sorter: false,
    },
    {
      title: ApplicationString['waitingList.attendeeAddedShort'] || 'Added',
      dataIndex: 'isAttendeeRegistered',
      key: 'isAttendeeRegistered',
      width: 72,
      align: 'center',
      sorter: false,
      render: (isAttendeeRegistered) => (isAttendeeRegistered ? 'Yes' : 'No'),
    },
    {
      title: ApplicationString['waitingList.cancelled'] || 'Cancelled',
      dataIndex: 'isCancelled',
      key: 'isCancelled',
      width: 88,
      align: 'center',
      sorter: false,
      render: (isCancelled) => (isCancelled ? 'Yes' : 'No'),
    },
    ...(canManageWaitingList
      ? [
          {
            title: ApplicationString['waitingList.actions'] || 'Actions',
            key: 'action',
            fixed: 'right',
            width: 88,
            align: 'center',
            render: (_, record) => {
              const isActive = isActiveWaitingListEntry(record);
              const isAnotherRowBusy =
                (movingWaitingListId != null &&
                  movingWaitingListId !== record.id) ||
                (cancellingWaitingListId != null &&
                  cancellingWaitingListId !== record.id);
              return (
                <AdminActionsDropdown
                  record={record}
                  text='Waiting List'
                  resource='events_registrations'
                  waitingListActionsBool
                  handleMoveToRegistrationClick={onMoveToRegistration}
                  handleCancelWaitingListClick={onCancelWaitingListEntry}
                  waitingListActionsDisabled={!isActive || isAnotherRowBusy}
                  dropdownDisabled={!isActive}
                />
              );
            },
          },
        ]
      : []),
  ];

  return (
    <div className='main-container'>
      <HeaderSearchCard
        showAddButton={false}
        toggleClick={toggleExpand}
        expanded={expanded}
        extra={
          <>
            {csvDataLoading && !waitingListDataForCSV?.length && (
              <Button>Fetching Waiting List..</Button>
            )}

            {!csvDataLoading && !waitingListDataForCSV?.length && (
              <Button
                onClick={() => {
                  fetchWaitingListForCSV(searchValues);
                }}>
                Load CSV Data
              </Button>
            )}

            {!csvDataLoading && waitingListDataForCSV?.length > 0 && (
              <Button>
                <CSVLink
                  data={formattedWaitingListCSVData}
                  headers={headers}
                  filename={'Waiting_List_Report.csv'}>
                  Download CSV
                </CSVLink>
              </Button>
            )}

            <Button
              onClick={() => navigate('/admin/events')}
              id='waitingList-button-backToEvents'>
              Back To Events
            </Button>
          </>
        }
      />
      {expanded && (
        <DynamicSearchForm
          columns={waitingListFilters}
          onSearch={onSearch}
          savedFilteredValues={searchValues}
          title={'waitingList'}
        />
      )}
      <div className='listing-container'>
        <Table
          bordered
          className='admin-table'
          columns={columns}
          dataSource={waitingListData}
          rowKey={'id'}
          scroll={{x: 1400}}
          sticky={true}
          onChange={handleTableChange}
          pagination={{
            onChange: (page, pageSize) => {
              handlePaginationChange(page, pageSize);
            },
            current: paging.page,
            total: paging.totalCount,
            pageSize: pageSize || paging.pagecount,
            pageSizeOptions: PageSizeArray,
            showSizeChanger: true,
            showQuickJumper: true,
            onShowSizeChange: (page, pageSize) => {
              handlePaginationChange(page, pageSize);
            },
            showTotal: (total) => `Total ${total} items`,
            position: [PaginationPosition],
          }}
        />
      </div>

      <Modal
        title={
          <>
            <InfoCircleOutlined /> Waiting List Notification
          </>
        }
        visible={showWaitingListMessage}
        onOk={handleWaitingListMessageClose}
        onCancel={handleWaitingListMessageClose}
        okText='Got it'>
        <p>
          This option is full. You will be added to the waiting list, and we
          will notify you by email if more spots become available.
        </p>
      </Modal>
    </div>
  );
};

export default WaitingListComponent;
